"use client";

import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { ShopShiftAssignmentRow } from "@/modules/merchant/presentation/api/client";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { computeShiftWeekStats } from "@/modules/merchant/presentation/lib/shift-assignment-status";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { ShopShiftList } from "@/modules/merchant/presentation/ui/shifts/ShopShiftList";
import { ShopShiftListFilters } from "@/modules/merchant/presentation/ui/shifts/ShopShiftListFilters";
import { ShopShiftListToolbar } from "@/modules/merchant/presentation/ui/shifts/ShopShiftListToolbar";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { formatDateLocal } from "@/shared/lib/date-time";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { useParams } from "@tanstack/react-router";
import {
  addWeeks,
  eachDayOfInterval,
  endOfWeek,
  format,
  startOfWeek,
} from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useShopBranchesListQuery,
  useShopShiftAssignmentsListQuery,
} from "../api/queries";

/** ລາຍການກະຕາມວັນ — ເບິ່ງປະຫວັດ (simple open/close ຈາກ POS) */
export function ShopShiftCalendarPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;

  const [weekOffset, setWeekOffset] = useState(0);
  const [branchId, setBranchId] = useState<string | undefined>(undefined);
  const [showEmptyDays, setShowEmptyDays] = useState(false);
  const [selectedDayKey, setSelectedDayKey] = useState<string | null>(null);
  const [scrollToDayKey, setScrollToDayKey] = useState<string | null>(null);

  const branchesQuery = useShopBranchesListQuery(shopId);
  const branches = branchesQuery.data ?? [];

  useEffect(() => {
    if (branches.length > 0 && !branchId) {
      setBranchId(branches[0]?.id);
    }
  }, [branches, branchId]);

  const weekDays = useMemo(() => {
    const start = startOfWeek(addWeeks(new Date(), weekOffset), {
      weekStartsOn: 1,
    });
    return eachDayOfInterval({
      start,
      end: endOfWeek(start, { weekStartsOn: 1 }),
    });
  }, [weekOffset]);

  const range = useMemo(() => {
    const from = format(weekDays[0] ?? new Date(), "yyyy-MM-dd");
    const to = format(weekDays[6] ?? new Date(), "yyyy-MM-dd");
    return { from, to };
  }, [weekDays]);

  const listQuery =
    branchId != null ? { branchId, from: range.from, to: range.to } : null;

  const assignments = useShopShiftAssignmentsListQuery(shopId, listQuery);

  const items = assignments.data?.items ?? [];

  const byDate = useMemo(() => {
    const m = new Map<string, ShopShiftAssignmentRow[]>();
    for (const day of weekDays) {
      m.set(format(day, "yyyy-MM-dd"), []);
    }
    for (const a of items) {
      const bucket = m.get(a.workDate);
      if (bucket) bucket.push(a);
    }
    for (const [, rows] of m) {
      rows.sort((x, y) => {
        const ax = x.clockInAt ?? x.scheduledStartAt;
        const bx = y.clockInAt ?? y.scheduledStartAt;
        return new Date(ax).getTime() - new Date(bx).getTime();
      });
    }
    return m;
  }, [items, weekDays]);

  const countsByDate = useMemo(() => {
    const m = new Map<string, number>();
    for (const [key, rows] of byDate) {
      m.set(
        key,
        rows.filter((r) => r.status !== "canceled").length,
      );
    }
    return m;
  }, [byDate]);

  const stats = useMemo(() => computeShiftWeekStats(items), [items]);

  const weekLabel = `${formatDateLocal(range.from)} – ${formatDateLocal(range.to)}`;

  const branchName =
    branches.find((b) => b.id === branchId)?.name ?? null;

  const handleSelectDay = useCallback((dateKey: string) => {
    setSelectedDayKey(dateKey);
    setScrollToDayKey(dateKey);
    if (!showEmptyDays && (countsByDate.get(dateKey) ?? 0) === 0) {
      setShowEmptyDays(true);
    }
  }, [countsByDate, showEmptyDays]);

  useEffect(() => {
    if (weekOffset === 0 && selectedDayKey == null) {
      const todayKey = format(new Date(), "yyyy-MM-dd");
      const todayInWeek = weekDays.some(
        (d) => format(d, "yyyy-MM-dd") === todayKey,
      );
      if (todayInWeek) {
        setSelectedDayKey(todayKey);
      }
    }
  }, [weekOffset, weekDays, selectedDayKey]);

  useEffect(() => {
    setSelectedDayKey(null);
    setScrollToDayKey(null);
  }, [weekOffset, branchId]);

  const handleRefresh = useCallback(() => {
    void assignments.refetch();
  }, [assignments]);

  return (
    <RequireMerchantPermission permission={MerchantPermissions.shifts.read}>
      <Header>
        <Breadcrumb items={shopBreadcrumbs.shiftCalendar(shopId)} />
      </Header>
      <Main>
        <ShopShiftListToolbar
          shopId={shopId}
          branchName={branchName}
          weekLabel={weekLabel}
          stats={stats}
          isRefreshing={assignments.isFetching && !assignments.isLoading}
          onRefresh={handleRefresh}
        />

        <ShopShiftListFilters
          branchId={branchId}
          branches={branches}
          onBranchChange={setBranchId}
          weekDays={weekDays}
          weekLabel={weekLabel}
          weekOffset={weekOffset}
          onWeekOffsetChange={setWeekOffset}
          countsByDate={countsByDate}
          selectedDayKey={selectedDayKey}
          onSelectDay={handleSelectDay}
          showEmptyDays={showEmptyDays}
          onShowEmptyDaysChange={setShowEmptyDays}
        />

        <ShopShiftList
          weekDays={weekDays}
          byDate={byDate}
          stats={stats}
          loading={assignments.isLoading}
          showEmptyDays={showEmptyDays}
          scrollToDayKey={scrollToDayKey}
          onScrolledToDay={() => setScrollToDayKey(null)}
        />
      </Main>
    </RequireMerchantPermission>
  );
}
